/**
 * AxLabs Chemistry Experiments - Electrochemistry
 *
 * Collection of electrochemistry experiments generated using
 * the declarative framework.
 */
import { createExperimentFromSchema, IDEAL_GAS_CONSTANT } from '../../framework/index.js';
import { getParam, getState, getStateValues } from '../../physics/generated/helpers.js';
// Faraday constant (C/mol)
const FARADAY = 96485.33212;
// ============================================================================
// GALVANIC CELL (NERNST EQUATION)
// ============================================================================
export const GalvanicCellNernst = createExperimentFromSchema({
    id: 'galvanic-cell-nernst',
    name: 'Galvanic Cell (Nernst Equation)',
    domain: 'chemistry',
    description: 'Electrochemical cell potential as a function of ion concentrations',
    conceptTags: ['Nernst Equation', 'Cell Potential', 'Redox Reactions', 'Gibbs Free Energy'],
    difficulty: 6,
    stateVariables: [
        { id: 'Q', name: 'Reaction Quotient', unit: '', initial: 1, symbol: 'Q', description: 'Ratio of product to reactant concentrations' },
    ],
    parameters: [
        { id: 'E_standard', name: 'Standard Cell Potential', unit: 'V', min: -3, max: 3, default: 1.1, symbol: 'E^\\circ', description: 'Standard cell potential (e.g., Daniell cell: 1.1 V)' },
        { id: 'n', name: 'Electrons Transferred', unit: '', min: 1, max: 10, default: 2, symbol: 'n', description: 'Number of moles of electrons transferred' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 273, max: 400, default: 298.15, symbol: 'T', description: 'System temperature' },
    ],
    equations: [
        {
            id: 'nernst-equation',
            name: 'Nernst Equation',
            latex: 'E = E^\\circ - \\frac{RT}{nF}\\ln Q',
            description: 'Cell potential as a function of reaction quotient',
        },
        {
            id: 'gibbs-free-energy',
            name: 'Gibbs Free Energy',
            latex: '\\Delta G = -nFE',
            description: 'Free energy change of the redox reaction',
        },
        {
            id: 'equilibrium-constant',
            name: 'Equilibrium Constant',
            latex: 'K = \\exp\\left(\\frac{nFE^\\circ}{RT}\\right)',
            description: 'Equilibrium constant at standard conditions',
        },
    ],
    derivatives: (_state, _params) => ({
        Q: 0,
    }),
    derivedState: (state, params) => {
        const Q = getState(state, 'Q');
        const E_standard = getParam(params, 'E_standard');
        const n = getParam(params, 'n');
        const T = getParam(params, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const F = FARADAY;
        const E = E_standard - (R * T) / (n * F) * Math.log(Q);
        const Delta_G = -n * F * E;
        const Delta_G_standard = -n * F * E_standard;
        const K = Math.exp((n * F * E_standard) / (R * T));
        return {
            E,
            Delta_G,
            Delta_G_standard,
            K,
            E_at_equilibrium: 0,
        };
    },
    failureModes: [
        {
            id: 'negative-quotient',
            name: 'Negative Reaction Quotient',
            check: (_state, _params) => {
                const Q = getState(getStateValues(_state), 'Q');
                return Q < 0;
            },
            severity: 'error',
            educationalMessage: 'The reaction quotient Q cannot be negative. It is the ratio of product activities to reactant activities.',
        },
        {
            id: 'zero-quotient',
            name: 'Zero Reaction Quotient',
            check: (_state, _params) => {
                const Q = getState(getStateValues(_state), 'Q');
                return Q === 0;
            },
            severity: 'error',
            educationalMessage: 'Q = 0 would require a reactant activity of zero, which is unphysical. All reactants must have some non-zero concentration.',
        },
    ],
    bounds: {
        Q: { min: 0.0001, max: 10000 },
    },
});
// ============================================================================
// ELECTROLYSIS (FARADAY'S LAW)
// ============================================================================
export const ElectrolysisFaraday = createExperimentFromSchema({
    id: 'electrolysis-faraday',
    name: 'Electrolysis (Faraday\'s Law)',
    domain: 'chemistry',
    description: 'Mass of substance deposited during electrolysis',
    conceptTags: ['Faraday\'s Laws', 'Electrolysis', 'Stoichiometry', 'Current Efficiency'],
    difficulty: 5,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't', description: 'Electrolysis duration' },
    ],
    parameters: [
        { id: 'I', name: 'Current', unit: 'A', min: 0.001, max: 10, default: 1, symbol: 'I', description: 'Electric current through the cell' },
        { id: 'n', name: 'Electrons per Ion', unit: '', min: 1, max: 10, default: 2, symbol: 'n', description: 'Number of electrons required to reduce one ion' },
        { id: 'M', name: 'Molar Mass', unit: 'g/mol', min: 1, max: 250, default: 63.55, symbol: 'M', description: 'Molar mass of the substance being deposited (e.g., Cu: 63.55 g/mol)' },
        { id: 'efficiency', name: 'Current Efficiency', unit: '', min: 0, max: 1, default: 1, symbol: '\\eta', description: 'Fraction of current used for the desired reaction' },
    ],
    equations: [
        {
            id: 'faraday-first-law',
            name: 'Faraday\'s First Law',
            latex: 'm = \\frac{M I t \\eta}{n F}',
            description: 'Mass deposited is proportional to charge passed',
        },
        {
            id: 'faraday-second-law',
            name: 'Faraday\'s Second Law',
            latex: '\\frac{m_1}{m_2} = \\frac{M_1/n_1}{M_2/n_2}',
            description: 'Masses of different substances deposited by same charge are proportional to equivalent weights',
        },
        {
            id: 'charge',
            name: 'Total Charge',
            latex: 'Q = I t',
            description: 'Total electric charge passed through the cell',
        },
    ],
    derivatives: (_state, _params) => ({
        t: 1,
    }),
    derivedState: (state, params) => {
        const t = getState(state, 't');
        const I = getParam(params, 'I');
        const n = getParam(params, 'n');
        const M = getParam(params, 'M');
        const efficiency = getParam(params, 'efficiency');
        const F = FARADAY;
        const Q = I * t;
        const moles_electrons = Q / F;
        const moles_substance = (moles_electrons * efficiency) / n;
        const mass = moles_substance * M;
        return {
            Q,
            moles_electrons,
            moles_substance,
            mass,
            charge_efficiency: efficiency * 100,
        };
    },
    failureModes: [
        {
            id: 'negative-time',
            name: 'Negative Time',
            check: (_state, _params) => {
                const t = getState(getStateValues(_state), 't');
                return t < 0;
            },
            severity: 'error',
            educationalMessage: 'Time cannot be negative.',
        },
        {
            id: 'zero-current',
            name: 'Zero Current',
            check: (_state, params) => {
                const I = getParam(params, 'I');
                return I <= 0;
            },
            severity: 'warning',
            educationalMessage: 'No current means no electrolysis is occurring. Increase the current to deposit material.',
        },
    ],
    bounds: {
        t: { min: 0, max: 86400 },
    },
});
// ============================================================================
// CONCENTRATION CELL
// ============================================================================
export const ConcentrationCell = createExperimentFromSchema({
    id: 'concentration-cell',
    name: 'Concentration Cell',
    domain: 'chemistry',
    description: 'Electrochemical cell driven by concentration difference',
    conceptTags: ['Concentration Cell', 'Nernst Equation', 'Cell Potential', 'Entropy of Mixing'],
    difficulty: 5,
    stateVariables: [
        { id: 'C_dilute', name: 'Dilute Half-Cell Concentration', unit: 'M', initial: 0.01, symbol: 'C_{dilute}', description: 'Ion concentration in dilute half-cell' },
    ],
    parameters: [
        { id: 'C_concentrated', name: 'Concentrated Half-Cell Concentration', unit: 'M', min: 0.1, max: 10, default: 1, symbol: 'C_{conc}' },
        { id: 'n', name: 'Electrons Transferred', unit: '', min: 1, max: 4, default: 1, symbol: 'n' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 273, max: 400, default: 298.15, symbol: 'T' },
    ],
    equations: [
        {
            id: 'concentration-cell-potential',
            name: 'Concentration Cell Potential',
            latex: 'E = \\frac{RT}{nF}\\ln\\left(\\frac{C_{conc}}{C_{dilute}}\\right)',
            description: 'Cell potential from concentration gradient',
        },
        {
            id: 'equilibrium',
            name: 'At Equilibrium',
            latex: 'E = 0 \\text{ when } C_{conc} = C_{dilute}',
            description: 'Cell potential is zero at equilibrium',
        },
    ],
    derivatives: (_state, _params) => ({
        C_dilute: 0,
    }),
    derivedState: (state, params) => {
        const C_dilute = getState(state, 'C_dilute');
        const C_conc = getParam(params, 'C_concentrated');
        const n = getParam(params, 'n');
        const T = getParam(params, 'T');
        const R = IDEAL_GAS_CONSTANT;
        const F = FARADAY;
        const E = (R * T) / (n * F) * Math.log(C_conc / C_dilute);
        const Delta_G = -n * F * E;
        const concentration_ratio = C_conc / C_dilute;
        return {
            E,
            Delta_G,
            concentration_ratio,
            E_at_25C: (R * 298.15) / (n * F) * Math.log(C_conc / C_dilute),
        };
    },
    failureModes: [
        {
            id: 'negative-concentration',
            name: 'Negative Concentration',
            check: (_state, _params) => {
                const C_dilute = getState(getStateValues(_state), 'C_dilute');
                return C_dilute < 0;
            },
            severity: 'error',
            educationalMessage: 'Concentration cannot be negative.',
        },
        {
            id: 'equal-concentrations',
            name: 'Equal Concentrations',
            check: (_state, params) => {
                const C_dilute = getState(getStateValues(_state), 'C_dilute');
                const C_conc = getParam(params, 'C_concentrated');
                return Math.abs(C_dilute - C_conc) / C_conc < 0.01;
            },
            severity: 'warning',
            educationalMessage: 'The concentrations are nearly equal, so the cell potential is very small. A concentration cell requires a concentration difference to generate voltage.',
        },
    ],
    bounds: {
        C_dilute: { min: 0.0001, max: 10 },
    },
});
// ============================================================================
// pH ELECTRODE CALIBRATION
// ============================================================================
export const pHElectrodeCalibration = createExperimentFromSchema({
    id: 'ph-electrode-calibration',
    name: 'pH Electrode Calibration',
    domain: 'chemistry',
    description: 'Nernstian response of a pH glass electrode',
    conceptTags: ['pH Electrode', 'Nernst Equation', 'Buffer Solutions', 'Calibration'],
    difficulty: 5,
    stateVariables: [
        { id: 'pH_solution', name: 'Solution pH', unit: '', initial: 7, symbol: 'pH', description: 'pH of the solution being measured' },
    ],
    parameters: [
        { id: 'E_standard', name: 'Standard Electrode Potential', unit: 'V', min: -0.5, max: 0.5, default: 0, symbol: 'E^\\circ', description: 'Electrode potential at pH 0' },
        { id: 'T', name: 'Temperature', unit: 'K', min: 273, max: 350, default: 298.15, symbol: 'T' },
        { id: 'slope_efficiency', name: 'Slope Efficiency', unit: '', min: 0.8, max: 1.05, default: 1, symbol: '\\eta', description: 'Fraction of theoretical Nernst slope (indicates electrode condition)' },
    ],
    equations: [
        {
            id: 'nernst-ph',
            name: 'Nernst Equation for pH Electrode',
            latex: 'E = E^\\circ - \\frac{2.303 RT}{F} \\cdot pH',
            description: 'Electrode potential as a function of pH',
        },
        {
            id: 'theoretical-slope',
            name: 'Theoretical Nernst Slope',
            latex: 'S = -\\frac{2.303 RT}{F} \\approx -59.16 \\text{ mV/pH at } 25^\\circ C',
            description: 'Theoretical slope at 25°C',
        },
        {
            id: 'actual-slope',
            name: 'Actual Slope',
            latex: 'S_{actual} = \\eta \\cdot S_{theoretical}',
            description: 'Measured slope (decreases with electrode aging)',
        },
    ],
    derivatives: (_state, _params) => ({
        pH_solution: 0,
    }),
    derivedState: (state, params) => {
        const pH = getState(state, 'pH_solution');
        const E_standard = getParam(params, 'E_standard');
        const T = getParam(params, 'T');
        const slope_efficiency = getParam(params, 'slope_efficiency');
        const R = IDEAL_GAS_CONSTANT;
        const F = FARADAY;
        const theoretical_slope = -(2.303 * R * T) / F;
        const actual_slope = theoretical_slope * slope_efficiency;
        const E = E_standard + actual_slope * pH;
        return {
            E,
            E_mV: E * 1000,
            theoretical_slope_mV: theoretical_slope * 1000,
            actual_slope_mV: actual_slope * 1000,
            H_concentration: Math.pow(10, -pH),
        };
    },
    failureModes: [
        {
            id: 'negative-ph',
            name: 'Negative pH',
            check: (_state, _params) => {
                const pH = getState(getStateValues(_state), 'pH_solution');
                return pH < 0;
            },
            severity: 'warning',
            educationalMessage: 'Negative pH is possible for very concentrated strong acids but is rare. Typical aqueous solutions have pH between 0 and 14.',
        },
        {
            id: 'high-ph',
            name: 'Very High pH',
            check: (_state, _params) => {
                const pH = getState(getStateValues(_state), 'pH_solution');
                return pH > 14;
            },
            severity: 'warning',
            educationalMessage: 'pH > 14 is possible for very concentrated strong bases but is uncommon. Typical aqueous solutions have pH between 0 and 14.',
        },
        {
            id: 'poor-electrode',
            name: 'Poor Electrode Condition',
            check: (_state, params) => {
                const slope_efficiency = getParam(params, 'slope_efficiency');
                return slope_efficiency < 0.9;
            },
            severity: 'warning',
            educationalMessage: 'The electrode slope efficiency is below 90%. The electrode may need cleaning or replacement.',
        },
    ],
    bounds: {
        pH_solution: { min: -1, max: 15 },
    },
});
// ============================================================================
// CONDUCTIVITY TITRATION
// ============================================================================
export const ConductivityTitration = createExperimentFromSchema({
    id: 'conductivity-titration',
    name: 'Conductivity Titration',
    domain: 'chemistry',
    description: 'Conductivity change during acid-base titration',
    conceptTags: ['Conductivity', 'Titration', 'Strong Electrolytes', 'Equivalent Point'],
    difficulty: 6,
    stateVariables: [
        { id: 'V_added', name: 'Titrant Volume Added', unit: 'mL', initial: 0, symbol: 'V_{added}', description: 'Volume of titrant added' },
    ],
    parameters: [
        { id: 'C_analyte', name: 'Analyte Concentration', unit: 'M', min: 0.01, max: 1, default: 0.1, symbol: 'C_{analyte}', description: 'Initial concentration of analyte' },
        { id: 'V_analyte', name: 'Analyte Volume', unit: 'mL', min: 10, max: 100, default: 50, symbol: 'V_{analyte}' },
        { id: 'C_titrant', name: 'Titrant Concentration', unit: 'M', min: 0.01, max: 2, default: 0.1, symbol: 'C_{titrant}', description: 'Concentration of titrant' },
        { id: 'lambda_H', name: 'H+ Molar Conductivity', unit: 'mS·m²/mol', min: 0, max: 400, default: 349.8, symbol: '\\lambda_{H^+}' },
        { id: 'lambda_OH', name: 'OH- Molar Conductivity', unit: 'mS·m²/mol', min: 0, max: 300, default: 198, symbol: '\\lambda_{OH^-}' },
        { id: 'lambda_anion', name: 'Anion Molar Conductivity', unit: 'mS·m²/mol', min: 20, max: 100, default: 76.3, symbol: '\\lambda_{anion}' },
        { id: 'lambda_cation', name: 'Cation Molar Conductivity', unit: 'mS·m²/mol', min: 20, max: 100, default: 50.1, symbol: '\\lambda_{cation}' },
    ],
    equations: [
        {
            id: 'conductivity',
            name: 'Solution Conductivity',
            latex: '\\kappa = \\sum_i \\lambda_i c_i',
            description: 'Total conductivity is sum of ionic contributions',
        },
        {
            id: 'equivalence-point',
            name: 'Equivalence Point Volume',
            latex: 'V_{eq} = \\frac{C_{analyte} V_{analyte}}{C_{titrant}}',
            description: 'Volume of titrant at equivalence point',
        },
    ],
    derivatives: (_state, _params) => ({
        V_added: 0,
    }),
    derivedState: (state, params) => {
        const V_added = getState(state, 'V_added');
        const C_analyte = getParam(params, 'C_analyte');
        const V_analyte = getParam(params, 'V_analyte');
        const C_titrant = getParam(params, 'C_titrant');
        const lambda_H = getParam(params, 'lambda_H');
        const lambda_OH = getParam(params, 'lambda_OH');
        const lambda_anion = getParam(params, 'lambda_anion');
        const lambda_cation = getParam(params, 'lambda_cation');
        const V_eq = (C_analyte * V_analyte) / C_titrant;
        const V_total = V_analyte + V_added;
        let kappa = 0;
        let excess_H = 0;
        let excess_OH = 0;
        if (V_added < V_eq) {
            // Before equivalence point: excess strong acid (analyte)
            const remaining_analyte_moles = C_analyte * V_analyte - C_titrant * V_added;
            excess_H = remaining_analyte_moles / V_total;
            const titrant_anion_moles = C_titrant * V_added;
            const anion_conc = titrant_anion_moles / V_total;
            const analyte_anion_moles = C_analyte * V_analyte;
            const analyte_anion_conc = analyte_anion_moles / V_total;
            kappa = (lambda_H * excess_H + lambda_anion * anion_conc + lambda_cation * analyte_anion_conc) / 1000;
        }
        else {
            // After equivalence point: excess strong base (titrant)
            const excess_titrant_moles = C_titrant * V_added - C_analyte * V_analyte;
            excess_OH = excess_titrant_moles / V_total;
            const all_anion_conc = (C_analyte * V_analyte) / V_total;
            const all_cation_conc = (C_titrant * V_added) / V_total;
            kappa = (lambda_OH * excess_OH + lambda_anion * all_anion_conc + lambda_cation * all_cation_conc) / 1000;
        }
        return {
            V_eq,
            V_total,
            kappa,
            kappa_mS_cm: kappa * 10,
            excess_H,
            excess_OH,
            at_equivalence: Math.abs(V_added - V_eq) < 0.1 ? 1 : 0,
        };
    },
    failureModes: [
        {
            id: 'negative-volume',
            name: 'Negative Volume Added',
            check: (_state, _params) => {
                const V_added = getState(getStateValues(_state), 'V_added');
                return V_added < 0;
            },
            severity: 'error',
            educationalMessage: 'Added volume cannot be negative.',
        },
        {
            id: 'excessive-dilution',
            name: 'Excessive Dilution',
            check: (_state, params) => {
                const V_added = getState(getStateValues(_state), 'V_added');
                const V_analyte = getParam(params, 'V_analyte');
                return V_added > V_analyte * 10;
            },
            severity: 'warning',
            educationalMessage: 'The solution is very diluted. Conductivity measurements become less accurate at high dilution due to low ion concentrations.',
        },
    ],
    bounds: {
        V_added: { min: 0, max: 200 },
    },
});
// ============================================================================
// ELECTROPLATING RATE
// ============================================================================
export const ElectroplatingRate = createExperimentFromSchema({
    id: 'electroplating-rate',
    name: 'Electroplating Rate',
    domain: 'chemistry',
    description: 'Rate of metal deposition during electroplating',
    conceptTags: ['Electroplating', 'Faraday\'s Law', 'Current Density', 'Deposition Rate'],
    difficulty: 5,
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 'I', name: 'Current', unit: 'A', min: 0.01, max: 50, default: 2, symbol: 'I' },
        { id: 'A', name: 'Electrode Area', unit: 'cm²', min: 1, max: 500, default: 50, symbol: 'A' },
        { id: 'n', name: 'Electrons per Ion', unit: '', min: 1, max: 4, default: 2, symbol: 'n' },
        { id: 'M', name: 'Molar Mass', unit: 'g/mol', min: 20, max: 250, default: 107.87, symbol: 'M', description: 'Silver: 107.87 g/mol' },
        { id: 'rho', name: 'Metal Density', unit: 'g/cm³', min: 1, max: 20, default: 10.49, symbol: '\\rho', description: 'Silver: 10.49 g/cm³' },
        { id: 'efficiency', name: 'Cathode Efficiency', unit: '', min: 0, max: 1, default: 0.95, symbol: '\\eta' },
    ],
    equations: [
        {
            id: 'deposition-mass',
            name: 'Deposited Mass',
            latex: 'm = \\frac{M I t \\eta}{n F}',
            description: 'Mass of metal deposited',
        },
        {
            id: 'deposition-thickness',
            name: 'Deposited Thickness',
            latex: 'd = \\frac{m}{\\rho A} = \\frac{M I t \\eta}{n F \\rho A}',
            description: 'Thickness of plated layer',
        },
        {
            id: 'deposition-rate',
            name: 'Deposition Rate',
            latex: 'r = \\frac{dd}{dt} = \\frac{M I \\eta}{n F \\rho A}',
            description: 'Rate of thickness increase',
        },
        {
            id: 'current-density',
            name: 'Current Density',
            latex: 'j = \\frac{I}{A}',
            description: 'Current per unit area',
        },
    ],
    derivatives: (_state, _params) => ({
        t: 1,
    }),
    derivedState: (state, params) => {
        const t = getState(state, 't');
        const I = getParam(params, 'I');
        const A = getParam(params, 'A');
        const n = getParam(params, 'n');
        const M = getParam(params, 'M');
        const rho = getParam(params, 'rho');
        const efficiency = getParam(params, 'efficiency');
        const F = FARADAY;
        const mass = (M * I * t * efficiency) / (n * F);
        const thickness = mass / (rho * A);
        const rate = (M * I * efficiency) / (n * F * rho * A);
        const current_density = I / A;
        return {
            mass,
            thickness_um: thickness * 10000,
            thickness,
            rate_um_s: rate * 10000,
            rate,
            current_density,
            current_density_A_dm2: current_density * 10,
        };
    },
    failureModes: [
        {
            id: 'negative-time',
            name: 'Negative Time',
            check: (_state, _params) => {
                const t = getState(getStateValues(_state), 't');
                return t < 0;
            },
            severity: 'error',
            educationalMessage: 'Time cannot be negative.',
        },
        {
            id: 'burning-limit',
            name: 'Current Density Too High',
            check: (_state, params) => {
                const I = getParam(params, 'I');
                const A = getParam(params, 'A');
                return I / A > 0.5;
            },
            severity: 'warning',
            educationalMessage: 'Current density exceeds 0.5 A/cm². This may cause "burning" of the deposit - rough, dark, or non-adherent coating. Reduce current or increase electrode area.',
        },
        {
            id: 'low-efficiency',
            name: 'Low Cathode Efficiency',
            check: (_state, params) => {
                const efficiency = getParam(params, 'efficiency');
                return efficiency < 0.8;
            },
            severity: 'warning',
            educationalMessage: 'Cathode efficiency is below 80%. This indicates significant side reactions (e.g., hydrogen evolution). Check bath chemistry and conditions.',
        },
    ],
    bounds: {
        t: { min: 0, max: 86400 },
    },
});
//# sourceMappingURL=electrochemistry.js.map